import React from 'react';
import { DialogBackdropConfig } from '../types';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LAYERS } from '@/utils/layers';
import { CentralizedMobileDetection } from '@/hooks/useCentralizedMobileDetection';

interface StandardDialogBackdropProps extends DialogBackdropConfig {
  isOpen: boolean;
  style?: React.CSSProperties;
  detection?: CentralizedMobileDetection;
}

export const StandardDialogBackdrop: React.FC<StandardDialogBackdropProps> = ({
  enabled = true,
  onClick,
  scaleTransform,
  oppositeTheme = true,
  content,
  showAsPreview = false,
  onNavigateLeft,
  onNavigateRight,
  canNavigateLeft = false,
  canNavigateRight = false,
  isOpen,
  style,
  detection
}) => {
  const viewport = detection || { isMobile: false };
  
  if (!enabled || !isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    console.log('🖱️ StandardDialogBackdrop: Click detected', {
      target: event.target,
      currentTarget: event.currentTarget,
      targetIsCurrentTarget: event.target === event.currentTarget,
      hasOnClick: !!onClick
    });
    
    if (event.target === event.currentTarget && onClick) {
      console.log('🖱️ StandardDialogBackdrop: Calling onClick handler');
      onClick(); // Don't pass event - interface expects no parameters
    } else {
      console.log('🖱️ StandardDialogBackdrop: Click ignored - target mismatch or no handler');
    }
  };

  const handlePreviewClick = (event: React.MouseEvent) => {
    if (onClick) {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    }
  };

  const handleNavigateLeft = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onNavigateLeft) {
      onNavigateLeft();
    }
  };

  const handleNavigateRight = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onNavigateRight) {
      onNavigateRight();
    }
  };

  const getBackdropClasses = () => {
    const isMobile = viewport.isMobile;
    if (oppositeTheme) {
      return isMobile 
        ? 'bg-black/40 dark:bg-white/40' 
        : 'bg-black/60 dark:bg-white/60';
    } else {
      return isMobile 
        ? 'bg-white/70 dark:bg-black/70' 
        : 'bg-white/90 dark:bg-black/90';
    }
  };

  // Preview mode: use scaling only in preview
  if (showAsPreview && content) {
    return (
      <div className="fixed inset-0" style={{ zIndex: LAYERS.DIALOG_BACKDROP }}>
        <div
          className={cn(
            'fixed inset-0 backdrop-blur-sm cursor-pointer',
            getBackdropClasses()
          )}
          style={{ zIndex: LAYERS.DIALOG_BACKDROP }}
          onClick={handleBackdropClick}
          role="button"
          tabIndex={-1}
          aria-label="Close dialog"
        />
        {(onNavigateLeft || onNavigateRight) && (
          <div className="fixed inset-x-0 top-0 h-[20vh] flex justify-between items-center px-4 pointer-events-none" style={{ zIndex: LAYERS.DIALOG_CONTENT }}>
            <button
              onClick={handleNavigateLeft}
              disabled={!canNavigateLeft}
              className={cn(
                "p-3 rounded-full bg-background/90 dark:bg-surface/90 text-foreground dark:text-foreground transition-all duration-200 pointer-events-auto border border-border/20",
                canNavigateLeft 
                  ? "hover:bg-background dark:hover:bg-surface opacity-100 hover:border-border/40" 
                  : "opacity-30 cursor-not-allowed"
              )}
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={handleNavigateRight}
              disabled={!canNavigateRight}
              className={cn(
                "p-3 rounded-full bg-background/90 dark:bg-surface/90 text-foreground dark:text-foreground transition-all duration-200 pointer-events-auto border border-border/20",
                canNavigateRight 
                  ? "hover:bg-background dark:hover:bg-surface opacity-100 hover:border-border/40" 
                  : "opacity-30 cursor-not-allowed"
              )}
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        )}
        <div 
          className="fixed inset-x-0 top-0 h-[20vh] overflow-hidden cursor-pointer"
          style={{ zIndex: LAYERS.DIALOG_CONTENT }}
          onClick={handlePreviewClick}
          role="button"
          tabIndex={-1}
          aria-label="Expand to full screen"
        >
          <div 
            className="w-full origin-top transform-gpu transition-transform duration-300 pointer-events-none"
            style={{
              transform: `scale(${scaleTransform ?? 0.3})`,
              transformOrigin: 'top center',
              height: `${100 / (scaleTransform ?? 0.3)}vh`
            }}
          >
            {content}
          </div>
        </div>
      </div>
    );
  }

  // Default backdrop: NO scaling!
  return (
    <div
      className={cn(
        'fixed inset-0 transition-all duration-300 ease-out backdrop-blur-sm cursor-pointer',
        getBackdropClasses()
      )}
      style={{
        zIndex: LAYERS.DIALOG_BACKDROP,
        ...style
      }}
      onClick={handleBackdropClick}
      role="button"
      tabIndex={-1}
      aria-label="Close dialog"
    />
  );
};
